import * as SheetPrimitive from '@radix-ui/react-dialog';
import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import * as React from 'react';
import { cn } from '@/lib/utils';
import { useVisualViewportInset } from '@/hooks/useVisualViewportInset';
import { useIsMobile } from '@/hooks/use-mobile';

const Sheet = SheetPrimitive.Root;
const SheetTrigger = SheetPrimitive.Trigger;
const SheetClose = SheetPrimitive.Close;
const SheetPortal = SheetPrimitive.Portal;
const SheetOverlay = React.forwardRef<React.ElementRef<typeof SheetPrimitive.Overlay>, React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>>(({
  className,
  ...props
}, ref) => <SheetPrimitive.Overlay className={cn('fixed inset-0 z-50 bg-black/30 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0', className)} {...props} ref={ref} />);
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;
const sheetVariants = cva('fixed z-50 gap-4 p-6 bg-background shadow-lg transition ease-standard data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-normal data-[state=open]:duration-normal [-webkit-tap-highlight-color:transparent]', {
  variants: {
    side: {
      top: 'inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top',
      bottom: 'inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
      left: 'inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm',
      right: 'inset-y-0 right-0 h-full w-3/4  border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm'
    }
  },
  defaultVariants: {
    side: 'right'
  }
});
interface SheetContentProps extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>, VariantProps<typeof sheetVariants> {
  showCloseButton?: boolean;
  /** Bottom sheets only: render a drag handle + enable swipe-down-to-close. Default true. */
  grabbable?: boolean;
  /** Bottom sheets only: optional control rendered inside the fixed grab-handle row. */
  grabHandleAccessory?: React.ReactNode;
  /** Bottom sheets only: classes for the generated body wrapper beneath the grab handle. */
  bodyClassName?: string;
}
const SheetContent = React.forwardRef<React.ElementRef<typeof SheetPrimitive.Content>, SheetContentProps>(({
  side = 'right',
  className,
  children,
  showCloseButton,
  grabbable = true,
  grabHandleAccessory,
  bodyClassName,
  ...props
}, ref) => {
  // Vertical grabbable sheets default to NO X (grab pill + swipe + backdrop tap handle dismissal).
  // Other sides default to showing the X.
  const resolvedShowClose = showCloseButton ?? !((side === 'bottom' || side === 'top') && grabbable);
  const isBottom = side === 'bottom';
  const isTop = side === 'top';
  const isVertical = isBottom || isTop;
  const innerRef = React.useRef<HTMLDivElement | null>(null);
  const [contentEl, setContentEl] = React.useState<HTMLDivElement | null>(null);
  const closeRef = React.useRef<HTMLButtonElement | null>(null);
  const dragStateRef = React.useRef({ y: 0, x: 0, t: 0, dy: 0, dragging: false, pointerId: null as number | null });
  const dragCleanupRef = React.useRef<() => void>(() => {});
  const setRefs = React.useCallback((node: HTMLDivElement | null) => {
    innerRef.current = node;
    setContentEl(node);
    if (typeof ref === 'function') ref(node);
    else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
  }, [ref]);

  const beginHandleDrag = React.useCallback((clientY: number, pointerId: number | null, preventDefault: () => void) => {
    const el = contentEl;
    if (!isVertical || !grabbable || !el) return;

    const resetDrag = () => {
      dragStateRef.current = { y: 0, x: 0, t: 0, dy: 0, dragging: false, pointerId: null };
    };

    dragCleanupRef.current();
    dragStateRef.current = { y: clientY, x: 0, t: performance.now(), dy: 0, dragging: false, pointerId };
    preventDefault();

    const finishByDistance = () => {
      const { dy, dragging, t } = dragStateRef.current;
      resetDrag();
      dragCleanupRef.current();
      if (!dragging) return;

      const dt = Math.max(1, performance.now() - t);
      const velocity = dy / dt;
      const shouldClose = isBottom
        ? dy > 80 || velocity > 0.5
        : dy < -80 || velocity < -0.5;
      el.style.transition = '';
      el.style.transform = '';
      if (shouldClose) closeRef.current?.click();
    };

    function moveTo(clientY: number, preventDefault: () => void) {
      const ady = clientY - dragStateRef.current.y;
      if (isBottom && ady <= 0 && !dragStateRef.current.dragging) return;
      if (isTop && ady >= 0 && !dragStateRef.current.dragging) return;
      preventDefault();
      dragStateRef.current.dragging = true;
      dragStateRef.current.dy = ady;
      el.style.transition = 'none';
      el.style.transform = `translateY(${ady}px)`;
    }

    function onMove(e: PointerEvent) {
      if (e.pointerId !== dragStateRef.current.pointerId) return;
      moveTo(e.clientY, () => e.preventDefault());
    }

    function onTouchMove(e: TouchEvent) {
      const touch = e.touches[0];
      if (!touch) return;
      moveTo(touch.clientY, () => e.preventDefault());
    }

    function finish(e: PointerEvent) {
      if (e.pointerId !== dragStateRef.current.pointerId) return;
      finishByDistance();
    }

    function finishTouch() {
      finishByDistance();
    }

    window.addEventListener('pointermove', onMove, { passive: false, capture: true });
    window.addEventListener('pointerup', finish, { passive: true, capture: true });
    window.addEventListener('pointercancel', finish, { passive: true, capture: true });
    window.addEventListener('touchmove', onTouchMove, { passive: false, capture: true });
    window.addEventListener('touchend', finishTouch, { passive: true, capture: true });
    window.addEventListener('touchcancel', finishTouch, { passive: true, capture: true });

    dragCleanupRef.current = () => {
      window.removeEventListener('pointermove', onMove, true);
      window.removeEventListener('pointerup', finish, true);
      window.removeEventListener('pointercancel', finish, true);
      window.removeEventListener('touchmove', onTouchMove, true);
      window.removeEventListener('touchend', finishTouch, true);
      window.removeEventListener('touchcancel', finishTouch, true);
      dragCleanupRef.current = () => {};
    };
  }, [contentEl, isBottom, grabbable]);

  React.useEffect(() => () => dragCleanupRef.current(), []);

  // For grabbable vertical sheets, the sheet root must NOT be the scroll container
  // (otherwise pointermoves on the grab handle get consumed as content scroll
  // and the drag-to-close gesture never engages). We strip any caller-supplied
  // overflow-y-* class and instead scroll an inner wrapper.
  const isGrabbableVertical = isVertical && grabbable;
  const sanitizedClassName = isBottom && grabbable
    ? (className ?? '').replace(/\boverflow-y-(auto|scroll)\b/g, '').trim()
    : className;

  // Keyboard-aware primitive: shift bottom sheets up above the on-screen
  // keyboard so the sheet's own submit CTA + inputs stay reachable without
  // every call site remembering to wire useVisualViewportInset. Non-bottom
  // sides unaffected. Callers can still override via `style` (spread after).
  const { keyboardHeight } = useVisualViewportInset();
  const kbStyle: React.CSSProperties | undefined =
    isBottom && keyboardHeight > 0 ? { bottom: `${keyboardHeight}px` } : undefined;

  // Prevent iOS keyboard from popping open the instant a mobile bottom sheet
  // opens: Radix Dialog auto-focuses the first focusable child, and if that's
  // a text input, the keyboard immediately covers half the screen before the
  // user can even parse what the sheet is. On mobile bottom sheets, focus the
  // sheet container instead — user taps a field to raise the keyboard.
  // Callers can still override by passing their own `onOpenAutoFocus`.
  const isMobile = useIsMobile();
  const callerOnOpenAutoFocus = props.onOpenAutoFocus;
  const defaultOnOpenAutoFocus = React.useCallback((e: Event) => {
    if (isBottom && isMobile) e.preventDefault();
  }, [isBottom, isMobile]);

  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Content
        ref={setRefs}
        className={cn(
          sheetVariants({ side }),
          isGrabbableVertical && 'flex flex-col overflow-hidden',
          sanitizedClassName,
        )}
        style={{ ...kbStyle, ...(props.style as React.CSSProperties | undefined) }}
        {...props}
        onOpenAutoFocus={callerOnOpenAutoFocus ?? defaultOnOpenAutoFocus}
      >

        {isGrabbableVertical && isBottom && (
          <>
            {/* Centered grab handle. touch-none + select-none + zero tap
                highlight keeps the pill from shifting/selecting under press. */}
            <div
              data-sheet-grab
              aria-hidden
              onPointerDown={(e) => beginHandleDrag(e.clientY, e.pointerId, () => e.preventDefault())}
              onTouchStart={(e) => {
                if ('PointerEvent' in window) return;
                const touch = e.touches[0];
                if (touch) beginHandleDrag(touch.clientY, null, () => e.preventDefault());
              }}
              onDragStart={(e) => e.preventDefault()}
              onContextMenu={(e) => e.preventDefault()}
              style={{ touchAction: 'none' }}
              className="relative -mx-6 mb-0 flex h-8 shrink-0 items-center justify-center cursor-grab active:cursor-grabbing select-none [-webkit-tap-highlight-color:transparent] [-webkit-touch-callout:none] [-webkit-user-select:none]"
            >
              <div className="h-1.5 w-12 rounded-full bg-white/25 pointer-events-none select-none" />
              {grabHandleAccessory && (
                <div className="pointer-events-auto absolute right-4 top-1/2 -translate-y-1/2">
                  {grabHandleAccessory}
                </div>
              )}
            </div>
            <SheetPrimitive.Close ref={closeRef} className="hidden" aria-hidden tabIndex={-1} />
          </>
        )}

        {isBottom && grabbable ? (
          <div className={cn('flex-1 min-h-0 overflow-y-auto -mx-6 px-6 overscroll-contain', bodyClassName)}>
            {children}
          </div>
        ) : (
          children
        )}

        {isGrabbableVertical && isTop && (
          <>
            <SheetPrimitive.Close ref={closeRef} className="hidden" aria-hidden tabIndex={-1} />
            {/* Top-sheet grab handle sits at the bottom edge so users can drag up to close. */}
            <div
              data-sheet-grab
              aria-hidden
              onPointerDown={(e) => beginHandleDrag(e.clientY, e.pointerId, () => e.preventDefault())}
              onTouchStart={(e) => {
                if ('PointerEvent' in window) return;
                const touch = e.touches[0];
                if (touch) beginHandleDrag(touch.clientY, null, () => e.preventDefault());
              }}
              onDragStart={(e) => e.preventDefault()}
              onContextMenu={(e) => e.preventDefault()}
              style={{ touchAction: 'none' }}
              className="relative -mx-6 mt-0 flex h-8 shrink-0 items-center justify-center cursor-grab active:cursor-grabbing select-none [-webkit-tap-highlight-color:transparent] [-webkit-touch-callout:none] [-webkit-user-select:none]"
            >
              <div className="h-1.5 w-12 rounded-full bg-white/25 pointer-events-none select-none" />
              {grabHandleAccessory && (
                <div className="pointer-events-auto absolute right-4 top-1/2 -translate-y-1/2">
                  {grabHandleAccessory}
                </div>
              )}
            </div>
          </>
        )}
        {resolvedShowClose && <SheetPrimitive.Close className="absolute right-4 top-4 rounded-md opacity-70 ring-offset-background transition-opacity data-[state=open]:bg-secondary hover:opacity-100 focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30 disabled:pointer-events-none">
          <X className="h-6 w-6" />
          <span className="sr-only">Close</span>
        </SheetPrimitive.Close>}
      </SheetPrimitive.Content>
    </SheetPortal>
  );
});
SheetContent.displayName = SheetPrimitive.Content.displayName;
const SheetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => <div className={cn('flex flex-col space-y-2 text-center sm:text-left', className)} {...props} />;
SheetHeader.displayName = 'SheetHeader';
const SheetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => <div className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)} {...props} />;
SheetFooter.displayName = 'SheetFooter';
const SheetTitle = React.forwardRef<React.ElementRef<typeof SheetPrimitive.Title>, React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>>(({
  className,
  ...props
}, ref) => <SheetPrimitive.Title ref={ref} className={cn('text-lg font-semibold text-foreground', className)} {...props} />);
SheetTitle.displayName = SheetPrimitive.Title.displayName;
const SheetDescription = React.forwardRef<React.ElementRef<typeof SheetPrimitive.Description>, React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>>(({
  className,
  ...props
}, ref) => <SheetPrimitive.Description ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />);
SheetDescription.displayName = SheetPrimitive.Description.displayName;
export { Sheet, SheetClose, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetOverlay, SheetPortal, SheetTitle, SheetTrigger };
